import { AuthService } from 'libs/acl/src/lib/auth.service';
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import {
  Observable,
  catchError,
  switchMap,
  throwError,
  from,
  timer,
  mergeMap,
} from 'rxjs';

@Injectable()
@Injectable()
export class AuthorizationInterceptor implements HttpInterceptor {
  constructor(private authService: AuthService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    // Skip interceptor for token requests
    if (this.isOAuthRequest(req)) {
      return next.handle(req);
    }

    if (!this.authService.hasInitializedValue()) {
      // Skip all intercepted logic until auth is ready
      return next.handle(req);
    }

    if (this.authService.hasValidAccessToken()) {
      return this.handleRequestWithToken(req, next);
    }

    const refreshToken = this.authService.getRefreshToken();
    if (!refreshToken) {
      this.authService.logout();
      return throwError(() => new Error('No refresh token. Logged out.'));
    }

    return from(this.authService.refreshToken()).pipe(
      switchMap((newToken) => {
        const authReq = req.clone({
          headers: req.headers.set('Authorization', `Bearer ${newToken}`),
        });
        return next.handle(authReq);
      }),
      catchError((error) => {
        // Handle token expiration or revocation
        const isInvalidGrant =
          error instanceof HttpErrorResponse &&
          error.status === 400 &&
          error.error?.error === 'invalid_grant';

        const isUnauthorized =
          error instanceof HttpErrorResponse && error.status === 401;

        if (isInvalidGrant || isUnauthorized) {
          console.warn(
            'Refresh token is invalid or user unauthorized, logging out.'
          );
          this.authService.logout();
          return throwError(
            () => new Error('Session expired. Please log in again.')
          );
        }

        // Let other errors fail fast (e.g. network disconnected)
        console.warn('Token refresh failed:', error.status, error.error);
        return throwError(() => error);
      })
    );
  }

  private isOAuthRequest(req: HttpRequest<any>): boolean {
    // Make this specific enough to avoid matching general API requests
    return (
      req.url.includes('/auth/refresh') || req.url.includes('/connect/token')
    );
  }

  private handleRequestWithToken(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    const token = this.authService.getAccessToken();
    const authReq = req.clone({
      headers: req.headers.set('Authorization', `Bearer ${token}`),
    });
    return next.handle(authReq);
  }
}
